import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Shield, Crown, UserPlus, Clock, X, UserMinus, UserCheck, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import type { CompanyMember } from "@/hooks/useCompanyAccount";
import { DemoHint } from "@/components/demo/DemoHint";
import { supabase } from "@/integrations/supabase/client";
import { useBillingMode } from "@/hooks/useBillingMode";

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  company_admin: "Admin",
  broker: "Broker",
  dispatcher: "Dispatcher",
  driver: "Driver",
  shipper: "Shipper",
  carrier: "Carrier",
};

interface PendingInvite {
  id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  expires_at: string;
}

interface Props {
  members: CompanyMember[];
  isAdmin: boolean;
  currentUserId: string;
  companyId: string;
  companyName: string;
  companyType: string;
  pendingInvites: PendingInvite[];
  seatsPurchased: number;
  seatsUsed: number;
  onInviteClick: () => void;
  onUpgradeClick?: () => void;
  onRefresh: () => void;
}

export function CompanyTeamTab({
  members,
  isAdmin,
  currentUserId,
  companyId,
  companyName,
  companyType,
  pendingInvites,
  seatsPurchased,
  seatsUsed,
  onInviteClick,
  onUpgradeClick,
  onRefresh,
}: Props) {
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const { isFree } = useBillingMode();

  const seatsAvailable = Math.max(0, seatsPurchased - seatsUsed - pendingInvites.length);

  const handleDeactivate = async (membershipId: string) => {
    setActionInProgress(membershipId);
    try {
      const { data, error } = await supabase.functions.invoke("manage-company-member", {
        body: { action: "deactivate", membershipId, companyId },
      });
      if (error) throw error;
      if (data?.error) { toast.error(data.error); return; }
      toast.success("Team member deactivated");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to deactivate member");
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReactivate = async (membershipId: string) => {
    setActionInProgress(membershipId);
    try {
      const { data, error } = await supabase.functions.invoke("manage-company-member", {
        body: { action: "reactivate", membershipId, companyId },
      });
      if (error) throw error;
      if (data?.error) { toast.error(data.error); return; }
      toast.success("Team member reactivated");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to reactivate member");
    } finally {
      setActionInProgress(null);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    setActionInProgress(inviteId);
    try {
      const { data, error } = await supabase.functions.invoke("manage-company-member", {
        body: { action: "cancel_invite", inviteId, companyId },
      });
      if (error) throw error;
      if (data?.error) { toast.error(data.error); return; }
      toast.success("Invitation cancelled");
      onRefresh();
    } catch (err: any) {
      toast.error("Failed to cancel invitation");
    } finally {
      setActionInProgress(null);
    }
  };

  const roleIcon = (role: string) => {
    if (role === "super_admin") return <Crown className="h-3.5 w-3.5 text-warning" />;
    if (role === "company_admin") return <Shield className="h-3.5 w-3.5 text-primary" />;
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Free tier panel */}
      {isFree ? (
        <Card className="p-4 sm:p-6 border-primary/20 bg-primary/5">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
              <Lock className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground">Free tier supports 1 user</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Upgrade to Premium to invite team members to {companyName}.
              </p>
              {isAdmin && onUpgradeClick && (
                <Button onClick={onUpgradeClick} size="sm" className="mt-4">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Upgrade to Premium
                </Button>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-2xl font-bold text-foreground">1 / 1</p>
              <p className="text-xs text-muted-foreground">Seat used</p>
            </div>
          </div>
        </Card>
      ) : (
        /* Seats overview (paid) */
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap">

              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">{seatsPurchased}</p>
                <p className="text-xs text-muted-foreground">Purchased</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">{seatsUsed}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
              {pendingInvites.length > 0 && (
                <div className="text-center">
                  <p className="text-2xl font-bold text-warning">{pendingInvites.length}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              )}
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">{seatsAvailable}</p>
                <p className="text-xs text-muted-foreground">Available</p>
              </div>
            </div>
            {isAdmin && (
              <Button onClick={onInviteClick} disabled={seatsAvailable <= 0} size="sm">
                <UserPlus className="h-4 w-4 mr-2" />
                Invite User
              </Button>
            )}
          </div>
          {seatsAvailable <= 0 && isAdmin && (
            <p className="text-xs text-destructive mt-2">
              All seats are in use. Purchase more seats to invite additional team members.
            </p>
          )}
        </Card>
      )}

      {/* Active members */}
      <Card className="p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <DemoHint hintKey="section.companyTeam" side="right" showIndicator>
              <h3 className="font-semibold text-foreground">Team Members</h3>
            </DemoHint>
            <p className="text-sm text-muted-foreground">{members.length} member{members.length !== 1 ? "s" : ""}</p>
          </div>
        </div>

        <div className="divide-y divide-border">
          {members.map((member) => {
            const name = member.profile
              ? [member.profile.first_name, member.profile.last_name].filter(Boolean).join(" ") || member.profile.email
              : member.user_id.slice(0, 8);
            const isSelf = member.user_id === currentUserId;
            const isPrimary = member.is_primary;
            const isActive = member.is_active !== false;

            return (
              <div key={member.id} className={`flex items-center justify-between py-3 gap-4 ${!isActive ? "opacity-50" : ""}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground flex-shrink-0">
                    {(member.profile?.first_name?.[0] || member.profile?.email?.[0] || "U").toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-foreground truncate">
                      {name} {isSelf && <span className="text-muted-foreground">(you)</span>}
                    </p>
                    {member.profile?.email && (
                      <p className="text-xs text-muted-foreground truncate">{member.profile.email}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {isPrimary && <Badge variant="secondary" className="text-[10px]">Primary</Badge>}
                  {!isActive && (
                    <Badge variant="secondary" className="bg-danger-subtle text-danger text-[10px]">Inactive</Badge>
                  )}
                  <div className="flex items-center gap-1.5">
                    {roleIcon(member.role)}
                    <span className="text-xs font-medium text-muted-foreground">
                      {ROLE_LABELS[member.role] || member.role}
                    </span>
                  </div>

                  {isAdmin && !isSelf && !isPrimary && (
                    <>
                      {isActive ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-destructive hover:text-destructive"
                          onClick={() => handleDeactivate(member.id)}
                          disabled={actionInProgress === member.id}
                        >
                          <UserMinus className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-primary hover:text-primary"
                          onClick={() => handleReactivate(member.id)}
                          disabled={actionInProgress === member.id}
                        >
                          <UserCheck className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {members.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">No team members found.</p>
        )}
      </Card>

      {/* Pending invites */}
      {pendingInvites.length > 0 && (
        <Card className="p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="h-5 w-5 text-warning" />
            <h3 className="font-semibold text-foreground">Pending Invitations</h3>
            <Badge variant="secondary">{pendingInvites.length}</Badge>
          </div>

          <div className="divide-y divide-border">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between py-3 gap-4">
                <div className="min-w-0">
                  <p className="font-medium text-sm text-foreground truncate">{invite.email}</p>
                  <p className="text-xs text-muted-foreground">
                    Sent {new Date(invite.created_at).toLocaleDateString()} · Expires {new Date(invite.expires_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge variant="secondary" className="text-[10px]">{ROLE_LABELS[invite.role] || invite.role}</Badge>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive"
                      onClick={() => handleCancelInvite(invite.id)}
                      disabled={actionInProgress === invite.id}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
